import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AnimatedSection } from '@/components/AnimatedSection';
import { useSEO } from '@/hooks/useSEO';
import { MessageSquare, FileText, Search, Calculator, ArrowRight, Phone, Mail, Clock, CheckCircle, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const bookingOptions = [
  {
    icon: MessageSquare,
    title: 'Ask a Tax Question - Free',
    description: 'Have a quick tax question? Start here for a simple, low-cost consultation with an experienced tax professional.',
    cta: 'Ask a Question',
    path: '/ask',
  },
  {
    icon: FileText,
    title: 'IRS Notice Review',
    description: 'Received a letter from the IRS? We\'ll review it, explain what it means, and help you respond before deadlines become costly.',
    cta: 'Get a Notice Review',
    path: '/services/irs-notice',
  },
  {
    icon: Search,
    title: 'Tax Diagnostic Review',
    description: 'Need a deeper look at your tax situation? Our paid diagnostic examines your documents and provides a clear action plan.',
    cta: 'Start a Tax Review',
    path: '/services/diagnostic',
  },
  {
    icon: Calculator,
    title: 'Tax Preparation Consultation',
    description: 'Ready to get your taxes filed? Book a consultation to discuss your filing needs, timeline, and pricing.',
    cta: 'Book Tax Prep',
    path: '/services/preparation',
  },
];

const serviceOptions = [
  { value: 'tax_question', label: 'Ask a Tax Question - Free' },
  { value: 'irs_notice_review', label: 'IRS Notice Review' },
  { value: 'tax_diagnostic', label: 'Tax Diagnostic Review' },
  { value: 'tax_preparation', label: 'Tax Preparation' },
  { value: 'tax_resolution', label: 'Tax Resolution' },
  { value: 'small_business', label: 'Small Business Tax Help' },
  { value: 'other', label: 'Other / Not Sure' },
];

export default function BookConsultation() {
  useSEO({
    title: 'Book a Consultation',
    description: 'Schedule a tax consultation with TaxPro - Answers. Ask a question, get an IRS notice review, start a tax diagnostic, or book tax preparation.',
  });

  const [form, setForm] = useState({ name: '', email: '', phone: '', question: '', service: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.question || !form.service) {
      toast.error('Please fill in all required fields including service type.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        question: form.question,
        type: form.service,
      });
      setSubmitted(true);
      toast.success('Your consultation request has been submitted. We will be in touch soon.');
    } catch {
      toast.error('Something went wrong. Please try again or call us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="book-consultation-page">
      {/* Hero */}
      <section className="bg-brand-navy py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-green mb-5">
              Book a Consultation
            </p>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight leading-none text-white mb-5">
              Take the First Step
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed">
              Choose the option that best fits your situation. Every engagement starts with a conversation — no commitment until you're ready.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Booking Options */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-green mb-4">
              Choose Your Service
            </p>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-brand-navy mb-12 max-w-lg">
              What kind of help do you need?
            </h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {bookingOptions.map((option, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <div className="border border-slate-200 rounded-lg p-8 h-full flex flex-col hover:border-brand-green/40 transition-colors group">
                  <div className="w-12 h-12 rounded-lg bg-brand-navy/5 flex items-center justify-center mb-6 group-hover:bg-brand-green/10 transition-colors">
                    <option.icon className="w-6 h-6 text-brand-navy group-hover:text-brand-green transition-colors" />
                  </div>
                  <h3 className="font-heading font-bold text-lg text-brand-navy mb-3">{option.title}</h3>
                  <p className="text-sm sm:text-[20px] font-semibold leading-relaxed sm:leading-[1.2] text-[#222222] mb-6 flex-1" style={{fontFamily: 'Inter, sans-serif'}}>{option.description}</p>
                  <Link to={option.path}>
                    <Button
                      data-testid={`book-option-${i}`}
                      className="w-full bg-brand-green hover:bg-brand-green-hover text-white font-semibold py-5 rounded-md"
                    >
                      {option.cta}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Setmore Booking */}
      <section data-testid="booking-calendar-section" className="py-20 lg:py-28 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <AnimatedSection>
            <CalendarDays className="w-12 h-12 text-brand-green mx-auto mb-5" />
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-brand-navy mb-4">
              Schedule Online
            </h2>
            <p className="text-base text-slate-500 mb-8 max-w-lg mx-auto">
              Prefer to book a specific time? Use our online scheduling tool to find a time that works for you.
            </p>
            <div data-testid="setmore-embed-live" className="rounded-lg overflow-hidden border border-slate-200">
              <iframe
                src="https://blmicker.setmore.com/YuFQOe0N3aLHNzVSCgUFbiioD9wpWqLu/service/159c98c6-37fd-4d2c-983a-fc1412b5f2f5"
                title="Schedule a Consultation"
                width="100%"
                height="700"
                frameBorder="0"
                className="w-full"
              />
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Contact Form + Contact Info */}
      <section className="py-20 lg:py-28 bg-brand-muted">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <AnimatedSection>
              {submitted ? (
                <div data-testid="book-success-message" className="bg-white border border-brand-green/20 rounded-lg p-10 text-center">
                  <CheckCircle className="w-12 h-12 text-brand-green mx-auto mb-4" />
                  <h3 className="font-heading font-semibold text-xl text-brand-navy mb-3">
                    Request Received
                  </h3>
                  <p className="text-sm sm:text-[20px] font-semibold leading-relaxed sm:leading-[1.2] text-[#222222] mb-6">
                    We've received your consultation request and will respond the same business day.
                  </p>
                  <Button
                    variant="outline"
                    className="border-brand-navy text-brand-navy"
                    onClick={() => { setSubmitted(false); setForm({ name: '', email: '', phone: '', question: '', service: '' }); }}
                  >
                    Submit Another Request
                  </Button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-testid="book-contact-form"
                  className="bg-white border border-slate-200 rounded-lg p-8"
                >
                  <h3 className="font-heading font-semibold text-lg text-brand-navy mb-2">
                    Request a Consultation
                  </h3>
                  <p className="text-sm sm:text-[20px] font-semibold leading-relaxed sm:leading-[1.2] text-[#222222] mb-6">
                    Fill out the form and we'll get back to you within 1 business day.
                  </p>
                  <div className="space-y-5">
                    <div>
                      <Label htmlFor="book-name" className="text-sm font-medium text-slate-700 mb-1.5 block">
                        Full Name *
                      </Label>
                      <Input
                        id="book-name"
                        data-testid="book-input-name"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder="John Doe"
                        className="bg-white"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="book-email" className="text-sm font-medium text-slate-700 mb-1.5 block">
                        Email Address *
                      </Label>
                      <Input
                        id="book-email"
                        data-testid="book-input-email"
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        placeholder="john@example.com"
                        className="bg-white"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="book-phone" className="text-sm font-medium text-slate-700 mb-1.5 block">
                        Telephone Number (Optional)
                      </Label>
                      <Input
                        id="book-phone"
                        data-testid="book-input-phone"
                        type="tel"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        placeholder="(555) 123-4567"
                        className="bg-white"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                        Service Type *
                      </Label>
                      <Select
                        value={form.service}
                        onValueChange={(val) => setForm({ ...form, service: val })}
                      >
                        <SelectTrigger data-testid="book-select-service" className="bg-white">
                          <SelectValue placeholder="Select a service..." />
                        </SelectTrigger>
                        <SelectContent>
                          {serviceOptions.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="book-message" className="text-sm font-medium text-slate-700 mb-1.5 block">
                        Tell Us About Your Situation *
                      </Label>
                      <Textarea
                        id="book-message"
                        data-testid="book-input-message"
                        value={form.question}
                        onChange={(e) => setForm({ ...form, question: e.target.value })}
                        placeholder="Briefly describe your tax question or concern..."
                        rows={4}
                        className="bg-white resize-none"
                        required
                      />
                    </div>
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="book-consent"
                        data-testid="book-consent-checkbox"
                        required
                        className="mt-1 h-4 w-4 rounded border-slate-300 text-brand-green focus:ring-brand-green flex-shrink-0"
                      />
                      <label htmlFor="book-consent" className="text-xs text-slate-500 leading-relaxed">
                        By clicking "Submit" I agree to receive emails, GENERAL COMMUNICATIONS, CUSTOMER SUPPORT, and MARKETING text messages, and phone calls from TAXPROS. I also agree to the Terms of Use and Privacy Policy linked below. Reply STOP to stop and HELP for help for more information. Message &amp; data rates may apply. Message frequency varies. <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-brand-green hover:underline font-medium">Privacy Policy</a>.
                      </label>
                    </div>
                    <Button
                      type="submit"
                      data-testid="book-submit-btn"
                      disabled={submitting}
                      className="w-full bg-brand-green hover:bg-brand-green-hover text-white font-semibold py-6 text-base rounded-md"
                    >
                      {submitting ? 'Submitting...' : 'Request a Consultation'}
                      {!submitting && <ArrowRight className="w-4 h-4 ml-2" />}
                    </Button>
                  </div>
                </form>
              )}
            </AnimatedSection>

            {/* Contact Info + What to Expect */}
            <div className="space-y-8">
              <AnimatedSection delay={0.1}>
                <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-brand-navy mb-4">
                  Prefer to talk directly?
                </h2>
                <p className="text-sm sm:text-[20px] font-semibold leading-relaxed sm:leading-[1.2] text-[#222222] leading-relaxed mb-6">
                  Call us or send an email. We respond within one business day.
                </p>
                <div className="space-y-4">
                  <a
                    href="tel:8555892461"
                    data-testid="book-phone-link"
                    className="flex items-center gap-4 p-5 bg-white border border-slate-200 rounded-lg hover:border-brand-green/30 transition-colors"
                  >
                    <div className="w-12 h-12 rounded-lg bg-brand-navy flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-brand-green" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-brand-navy">Call Us</p>
                      <p className="text-base font-heading font-bold text-brand-navy">855-589-2461</p>
                    </div>
                  </a>
                  <a
                    href="mailto:info@taxpro-answers.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="book-email-link"
                    className="flex items-center gap-4 p-5 bg-white border border-slate-200 rounded-lg hover:border-brand-green/30 transition-colors cursor-pointer"
                  >
                    <div className="w-12 h-12 rounded-lg bg-brand-navy flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-brand-green" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-brand-navy">Email Us</p>
                      <p className="text-base font-heading font-bold text-brand-navy">info@taxpro-answers.com</p>
                    </div>
                  </a>
                </div>
              </AnimatedSection>

              <AnimatedSection delay={0.2}>
                <div className="bg-brand-navy rounded-lg p-8 text-white">
                  <Clock className="w-8 h-8 text-brand-green mb-4" />
                  <h3 className="font-heading font-bold text-xl mb-4">What to Expect</h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <span className="font-heading font-bold text-brand-green">1.</span>
                      <p className="text-sm text-slate-300 leading-relaxed">
                        <span className="text-white font-medium">Contact us</span> — by phone, email, or the form on this page.
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="font-heading font-bold text-brand-green">2.</span>
                      <p className="text-sm text-slate-300 leading-relaxed">
                        <span className="text-white font-medium">Brief conversation</span> — we'll discuss your situation and identify the right service.
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="font-heading font-bold text-brand-green">3.</span>
                      <p className="text-sm text-slate-300 leading-relaxed">
                        <span className="text-white font-medium">Clear next steps</span> — you'll know exactly what to expect, including timing and cost.
                      </p>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
